/**
 * VCloud register form component
 * Features: Provider/Zone selection + auto registration
 */
import LoadingBox from '@/components/Loading/LoadingBox'
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import {
    getProviders,
    getServiceInformation,
    registerService,
    updateServiceInformations,
} from '@/modules/Service/services'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { AlertError, GridHookForm } from 'vnetwork-ui'
import * as yup from 'yup'
import { QUERY_KEYS, QUERY_KEY_COMBINATIONS } from '@/constants/reactQueryKeys'
import type { RegisterServiceTemplateProps } from '../types'

// Schema
const VCloudRegisterSchema = yup.object({
    provider_id: yup.string().required(),
    zone_id: yup.string().required(),
})

const VCloudRegisterForm: React.FC<RegisterServiceTemplateProps> = ({
    service,
}) => {
    const serviceTemplate = service?.service_template
    const serviceID = service?.service_id as number
    const serviceUUID = service?.service_creatable_uuid
    const { t } = useTranslation()

    const formControl = useForm({
        defaultValues: {
            provider_id: '',
            zone_id: '',
        },
        resolver: yupResolver(VCloudRegisterSchema),
    })

    const { refetch: refetchTickets } = useQueryClientCallback({
        pattern: 'tickets',
        mode: 'contains',
    })
    const { refetch: refetchServices } = useQueryClientCallback({
        pattern: 'services',
        mode: 'contains',
    })

    const { mutateAsync: registerServiceMutation, isPending: isRegistering } =
        useMutationWithHandler(
            { mutationFn: registerService },
            {
                successMessage: t('register-service-success'),
                reactQueryKeys: QUERY_KEY_COMBINATIONS.SERVICE_WITH_TICKET,
                onSuccess: () => {
                    refetchTickets()
                    refetchServices()
                },
            }
        )
    const { mutateAsync, isPending } = useMutationWithHandler(
        { mutationFn: updateServiceInformations },
        {
            successMessage: t('update-provider-and-zone-information-success'),
            reactQueryKeys: QUERY_KEYS.SERVICES,
            onSuccess: () => {
                refetchServiceInformation()
            },
        }
    )

    const {
        data: serviceInformation,
        isLoading: isServiceInformationLoading,
        isError: isServiceInformationError,
        error: serviceInformationError,
        refetch: refetchServiceInformation,
    } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.SERVICES_INFORMATIONS, serviceID],
        queryFn: () => getServiceInformation(serviceID),
    })

    const {
        data: providers,
        isLoading,
        isError,
        error,
    } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.PROVIDERS],
        queryFn: () => getProviders(),
    })

    const provider_id = formControl.watch('provider_id')

    const zone = serviceInformation?.results?.find(
        (info) => info.name === 'zone'
    )?.value

    const provider = serviceInformation?.results?.find(
        (info) => info.name === 'provider'
    )?.value

    const providerOptions = useMemo(() => {
        return (
            providers
                ?.filter(
                    (provider) => provider.creatable_type === serviceTemplate
                )
                ?.map((provider) => ({
                    label: provider.scope,
                    value: provider.id,
                })) || []
        )
    }, [providers])

    const zoneOptions = useMemo(() => {
        return (
            providers
                ?.find((provider) => provider.id === (provider_id as string))
                ?.zones?.map((zone) => ({
                    label: `${zone.name} - (${zone.description})`,
                    value: zone.id,
                })) || []
        )
    }, [providers, provider_id])

    useEffect(() => {
        provider && formControl.setValue('provider_id', provider)
        zone && formControl.setValue('zone_id', zone)
    }, [provider, zone])

    if (isError || isServiceInformationError)
        return (
            <AlertError error={error || (serviceInformationError as Error)} />
        )

    if (isLoading || isServiceInformationLoading) return <LoadingBox />

    return (
        <div className="space-y-4">
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800 font-medium mb-2">
                    {t('select-provider-and-zone-for-vcloud')}
                </p>
                <p className="text-xs text-blue-600">
                    {t('configure-provider-and-zone-before-registration')}
                </p>
            </div>
            <div className="mt-2">
                <GridHookForm
                    data={[
                        {
                            formItemProps: {
                                name: 'provider_id',
                                label: t('provider'),
                                type: 'select',
                                placeholder: t('select-provider'),
                                options: providerOptions || [],
                                disabled: !!serviceUUID,
                            },
                        },
                        {
                            formItemProps: {
                                name: 'zone_id',
                                label: t('Zone'),
                                type: 'select',
                                placeholder: t('Zone-placeholder'),
                                options: zoneOptions || [],
                                disabled: !provider_id || !!serviceUUID,
                            },
                        },
                    ]}
                    onSubmit={async (values) => {
                        await mutateAsync({
                            serviceID,
                            data: [
                                {
                                    name: 'provider',
                                    value: values.provider_id,
                                },
                                {
                                    name: 'zone',
                                    value: values.zone_id,
                                },
                            ],
                            message: 'Update provider and zone information',
                        })
                        await registerServiceMutation(serviceID)
                    }}
                    actions={[
                        {
                            type: 'submit',
                            title: t('save-and-register'),
                            variant: 'contained',
                            loading: isPending || isRegistering,
                            disabled:
                                isPending || isRegistering || !!serviceUUID,
                        },
                    ]}
                    formControl={formControl}
                />
            </div>
        </div>
    )
}

export default VCloudRegisterForm
